import React from 'react'
import { Icon } from './index.jsx'
import toast from 'react-hot-toast'
import { useClickOutside } from './useClickOutside.js'
import { tintBg, tintFg } from '../lib/tint'

/* ============================================================
   Shared UI components — match osct-2 design system
   Exports: Card, Btn, Badge, Avatar, Progress, Stat, Tabs,
            Segmented, TableWrap, ProgramBanner,
            Sparkline, LevelBadge, LEVEL_COLORS, ExportMenu, QrModal
   ============================================================ */

// ── Card ─────────────────────────────────────────────────────
// Use for any content block. title+action render a card-header.
// noPad skips card-body wrapper (for full-bleed content).
export function Card({ title, action, eyebrow, children, tight, noPad, className = '' }) {
  return (
    <div className={`card ${className}`}>
      {(title || action || eyebrow) && (
        <div className="card-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            {eyebrow && <div className="eyebrow" style={{ marginBottom: 2 }}>{eyebrow}</div>}
            {title && <div className="h3">{title}</div>}
          </div>
          {action}
        </div>
      )}
      {noPad ? children : <div className={`card-body${tight ? ' tight' : ''}`}>{children}</div>}
    </div>
  )
}

// ── Btn ──────────────────────────────────────────────────────
// variant: 'primary' | 'secondary' | 'ghost' | 'danger'
// size: 'sm' | 'lg' | undefined (default)
export function Btn({ variant = 'secondary', size, block, icon, children, className = '', ...rest }) {
  return (
    <button
      className={`btn btn-${variant}${size ? ` btn-${size}` : ''}${block ? ' btn-block' : ''} ${className}`}
      {...rest}
    >
      {icon && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>}
      {children}
    </button>
  )
}

// ── Badge ────────────────────────────────────────────────────
// tone: 'default' | 'success' | 'warning' | 'danger' | 'brand' | 'outline'
export function Badge({ tone = 'default', dot, children, className = '' }) {
  return (
    <span className={`badge${tone !== 'default' ? ` ${tone}` : ''} ${className}`}>
      {dot && <span className="badge-dot" />}
      {children}
    </span>
  )
}

// ── Avatar ───────────────────────────────────────────────────
// Shows 1–2 initials with a soft background derived from the name.
// Satu warna dasar per avatar; latar dan teks diturunkan lewat color-mix (lib/tint)
// supaya ikut tema: pastel di tema terang, arang berwarna di tema gelap.
const AVATAR_COLORS = ['#086568', '#9F6500', '#143553', '#8E2D22', '#4A3E80', '#1F5C3E']
const AVATAR_PALETTE = AVATAR_COLORS.map((c) => [tintBg(c, 16), tintFg(c, 72)])
export function Avatar({ name = '?', size = 28, colors }) {
  const initials = String(name)
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0] || '')
    .join('')
    .toUpperCase() || '?'
  const idx = (name.charCodeAt(0) || 0) % AVATAR_PALETTE.length
  const [bg, fg] = colors || AVATAR_PALETTE[idx]
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, background: bg, color: fg, fontSize: Math.round(size * 0.4) }}
    >
      {initials}
    </span>
  )
}

// ── Progress ─────────────────────────────────────────────────
// value 0–100 (or 0–max). Optionally pass color to override accent.
export function Progress({ value, max = 100, color, style }) {
  const pct = Math.min(100, Math.round((value / (max || 1)) * 100))
  return (
    <div className="progress" style={style}>
      <div
        className="progress-bar"
        style={{ width: `${pct}%`, ...(color ? { background: color } : {}) }}
      />
    </div>
  )
}

// ── Stat ─────────────────────────────────────────────────────
// Matches design's stat-card pattern.
// deltaDir: 'up' | 'down' (default 'up')
export function Stat({ label, value, delta, deltaDir = 'up', sub, hint }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {(delta || sub || hint) && (
        <div className="row" style={{ marginTop: 6, gap: 8, flexWrap: 'wrap' }}>
          {delta && (
            <span className={`stat-delta ${deltaDir === 'down' ? 'down' : 'up'}`}>
              {deltaDir === 'down' ? '↓' : '↑'} {delta}
            </span>
          )}
          {(sub || hint) && (
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{sub || hint}</span>
          )}
        </div>
      )}
    </div>
  )
}

// ── Tabs ─────────────────────────────────────────────────────
// tabs: [{ id, label, count? }]
export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          className={`tab${value === t.id ? ' active' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count != null && <span className="tab-count">{t.count}</span>}
        </button>
      ))}
    </div>
  )
}

// ── Segmented ────────────────────────────────────────────────
// options: [{ value, label }]
export function Segmented({ options, value, onChange }) {
  return (
    <div className="segmented">
      {options.map((o) => (
        <button
          key={o.value}
          className={value === o.value ? 'active' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ── TableWrap ────────────────────────────────────────────────
// Horizontal-scroll wrapper for wide tables on mobile.
export function TableWrap({ children }) {
  return <div className="table-scroll">{children}</div>
}

// ── ProgramBanner ────────────────────────────────────────────
// Visual identity header for the active program (FR-10.1).
export function ProgramBanner({ program, lang = 'id' }) {
  if (!program) return null
  const color = program.color_theme || 'var(--program-color)'
  return (
    <div
      className="card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 20px',
        borderLeft: `3px solid ${color}`,
        background: color + '10',
        marginBottom: 16,
        borderRadius: 'var(--r-lg)',
      }}
    >
      <div
        style={{
          width: 48, height: 48, borderRadius: 'var(--r-md)',
          display: 'grid', placeItems: 'center',
          background: color + '20', color,
          flexShrink: 0,
        }}
      >
        {program.icon
          ? <Icon name={program.icon} size={22} color={color} />
          : <span className="mono" style={{ fontWeight: 700, fontSize: 16 }}>L{program.level}</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="eyebrow" style={{ color }}>
          {(program.code || '').toUpperCase()} · LEVEL {program.level}
        </div>
        <div className="h2" style={{ marginTop: 2 }}>{program.name}</div>
        {program.description && (
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }} title={program.description}>
            {program.description.length > 80 ? program.description.slice(0, 80) + '…' : program.description}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Sparkline ────────────────────────────────────────────────
// SVG polyline sparkline with area fill.
export function Sparkline({ points, w = 100, h = 28, color }) {
  const max = Math.max(...points)
  const min = Math.min(...points)
  const range = max - min || 1
  const stepX = w / (points.length - 1)
  const pts = points
    .map((p, i) => `${i * stepX},${h - ((p - min) / range) * h}`)
    .join(' ')
  const c = color || 'var(--program-color)'
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline
        points={pts}
        fill="none"
        stroke={c}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={`0,${h} ${pts} ${w},${h}`}
        fill={c}
        fillOpacity="0.08"
      />
    </svg>
  )
}

// ── LevelBadge ───────────────────────────────────────────────
// Program tier pill (L1 / L2 / …) with per-tier colour.
// Not every programme has tiers — a factory's one-off safety course has none —
// so this renders nothing at all when `level` is absent, rather than showing
// a misleading "L1". Pass `label` to use the institution's own wording.
// Cadangan saat sebuah program tidak mengirimkan warnanya. Nilainya HARUS sama
// dengan color_theme di prisma/seed.js — ini salinan kedua dari daftar yang
// sama, dan kalau hanya salah satu diubah, lencana level akan berwarna lain
// daripada tombol di layar yang sama.
export const LEVEL_COLORS = { 1: '#7A6A45', 2: '#0F62FE', 3: '#3E4A50' }
export function LevelBadge({ level, big, programColor, label }) {
  if (level == null && !label) return null
  const color = programColor || LEVEL_COLORS[level] || 'var(--text-3)'
  return (
    <span
      className="badge"
      style={{
        background: color + '20',
        color: `color-mix(in srgb, ${color} 60%, var(--text))`,
        border: `1px solid ${color}40`,
        fontSize: big ? 12 : 11,
        fontWeight: 600,
        padding: big ? '3px 10px' : '2px 7px',
      }}
    >
      {label || `L${level}`}
    </span>
  )
}

// ── ExportMenu ───────────────────────────────────────────────
// Satu tombol "Unduh" yang membuka panel format (CSV/Excel/PDF), menggantikan
// tombol terpisah per format yang tadinya berjajar di layar Laporan.
//
// `kindPrefix` + format yang dipilih menyusun kind yang dikirim ke onSelect —
// mis. kindPrefix="participants" + Excel menjadi "participants-xlsx" — pola
// yang sudah dipakai dispatch map di ReportsScreen sejak awal, jadi menambah
// menu ini tidak menuntut mengubah cara kind diproses di sana.
//
// Panel melayang meniru program-switcher/program-dropdown di App.jsx
// (posisi absolut, box-shadow, z-index), dan tutup-saat-klik-di-luar meniru
// NotificationsPanel — dua pola yang sudah ada, disatukan di sini.
const FORMAT_LABEL = { csv: 'CSV', xlsx: 'Excel', pdf: 'PDF' }

export function ExportMenu({ formats, kindPrefix, busyKind, onSelect, lang = 'id', label, variant = 'secondary' }) {
  const [open, setOpen] = React.useState(false)
  const ref = useClickOutside(open, setOpen)

  const kindUntuk = (format) => `${kindPrefix}-${format}`
  const sedangUnduh = busyKind != null && formats.some((f) => busyKind === kindUntuk(f))

  return (
    <div className="export-menu" ref={ref}>
      <Btn
        variant={variant}
        icon={<Icon name="download" size={14} />}
        disabled={busyKind != null}
        onClick={() => setOpen((o) => !o)}
      >
        {sedangUnduh
          ? (lang === 'id' ? 'Mengunduh...' : 'Downloading...')
          : (label || (lang === 'id' ? 'Unduh' : 'Download'))}
        <Icon name="chevron_down" size={12} />
      </Btn>
      {open && (
        <div className="export-menu-panel">
          {formats.map((f) => (
            <button
              key={f}
              className="export-menu-item"
              disabled={busyKind != null}
              onClick={() => { setOpen(false); onSelect(kindUntuk(f)) }}
            >
              {FORMAT_LABEL[f]}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── ExportMenuGroups ─────────────────────────────────────────
// Satu tombol "Unduh" untuk SEMUA berkas yang bisa dibuat dari sebuah layar.
// Tiap bagian = satu jenis berkas, dengan judul, satu baris penjelasan, dan
// pilihan formatnya. Menggantikan beberapa ExportMenu terpisah yang tadinya
// tersebar di layar (satu di header, lainnya di bawah tabel dengan paragraf
// penjelasan panjang) — kini semua unduhan ada di satu tempat yang sama di
// setiap tab, dan penjelasan format berkas hidup di sebelah pilihannya.
//
// sections: [{ key, title, hint?, kindPrefix, formats }]
export function ExportMenuGroups({ sections, busyKind, onSelect, lang = 'id', label, variant = 'secondary', footer, disabled = false, disabledTitle }) {
  const [open, setOpen] = React.useState(false)
  const ref = useClickOutside(open, setOpen)

  const sedangUnduh =
    busyKind != null &&
    sections.some((s) => s.formats.some((f) => busyKind === `${s.kindPrefix}-${f}`))

  return (
    <div className="export-menu" ref={ref}>
      <Btn
        variant={variant}
        icon={<Icon name="download" size={14} />}
        disabled={busyKind != null || disabled}
        title={disabled ? disabledTitle : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {sedangUnduh
          ? (lang === 'id' ? 'Mengunduh...' : 'Downloading...')
          : (label || (lang === 'id' ? 'Unduh laporan' : 'Download'))}
        <Icon name="chevron_down" size={12} />
      </Btn>
      {open && (
        <div className="export-menu-panel export-menu-panel-wide">
          {sections.map((s, i) => (
            <div key={s.key || s.kindPrefix} className={`export-menu-section${i > 0 ? ' with-divider' : ''}`}>
              <div className="export-menu-section-title">{s.title}</div>
              {s.hint && <div className="export-menu-section-hint">{s.hint}</div>}
              <div className="export-menu-formats">
                {s.formats.map((f) => (
                  <button
                    key={f}
                    className="export-menu-fmt"
                    disabled={busyKind != null}
                    onClick={() => { setOpen(false); onSelect(`${s.kindPrefix}-${f}`) }}
                  >
                    {FORMAT_LABEL[f]}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {footer && <div className="export-menu-footer">{footer}</div>}
        </div>
      )}
    </div>
  )
}

// ── QrModal ──────────────────────────────────────────────────────────────────
// QR untuk diproyeksikan ke kelas. Isinya URL biasa tanpa rahasia apa pun, jadi
// satu QR berlaku untuk seluruh peserta sekaligus — tidak perlu satu per orang.
// Peserta tetap login sendiri setelah memindai.
//
// Menerima data yang SUDAH diambil, bukan mengambil sendiri: dengan begitu ia
// tidak terikat modul API mana pun, dan pemanggil bebas memakai gaya stringnya
// masing-masing (Editor Tes memakai ternary lang, layar Feedback memakai useT).
export function QrModal({ title, url, qrPngDataUrl, downloadName, hint, error, onClose, lang = 'id' }) {
  const kartuRef = React.useRef(null)

  const salinTautan = () => {
    navigator.clipboard?.writeText(url)
    toast.success(lang === 'id' ? 'Tautan disalin' : 'Link copied')
  }

  // Untuk proyektor. Idiom yang sama dipakai pemutar video dan pembaca PDF.
  const layarPenuh = () => {
    const el = kartuRef.current
    if (!el) return
    if (document.fullscreenElement) document.exitFullscreen?.()
    else el.requestFullscreen?.()
  }

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
        display: 'grid', placeItems: 'center', zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 460, maxWidth: '92vw', background: 'var(--surface)' }}
      >
        <div className="card-header">
          <div className="h3">{title}</div>
          <Btn variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={14} /></Btn>
        </div>

        <div
          ref={kartuRef}
          className="col"
          style={{ alignItems: 'center', padding: 20, gap: 12, background: 'var(--surface)' }}
        >
          {error ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center' }}>{error}</div>
          ) : !qrPngDataUrl ? (
            <div className="muted" style={{ padding: 24 }}>
              {lang === 'id' ? 'Memuat...' : 'Loading...'}
            </div>
          ) : (
            <>
              {/* Latar putih dipatok, bukan var(--surface): dalam mode gelap QR
                  di atas latar gelap tidak terbaca pemindai mana pun. */}
              <div style={{ background: '#fff', padding: 12, borderRadius: 8 }}>
                <img
                  src={qrPngDataUrl}
                  alt={title}
                  style={{ width: 'min(300px, 60vw)', aspectRatio: '1 / 1', display: 'block' }}
                />
              </div>
              <div className="col" style={{ alignItems: 'center', gap: 4, textAlign: 'center' }}>
                <span className="tiny muted">
                  {hint || (lang === 'id'
                    ? 'Peserta memindai dengan kamera HP, lalu login seperti biasa.'
                    : 'Participants scan with their phone camera, then sign in as usual.')}
                </span>
                <span className="tiny mono" style={{ wordBreak: 'break-all' }}>{url}</span>
              </div>
            </>
          )}
        </div>

        {qrPngDataUrl && (
          <div style={{
            padding: '12px 16px', borderTop: '1px solid var(--border-subtle)',
            display: 'flex', justifyContent: 'flex-end', gap: 8,
          }}>
            <Btn variant="ghost" size="sm" icon={<Icon name="copy" size={12} />} onClick={salinTautan}>
              {lang === 'id' ? 'Salin tautan' : 'Copy link'}
            </Btn>
            <Btn variant="ghost" size="sm" icon={<Icon name="fullscreen" size={12} />} onClick={layarPenuh}>
              {lang === 'id' ? 'Layar penuh' : 'Fullscreen'}
            </Btn>
            <a
              href={qrPngDataUrl}
              download={downloadName}
              style={{
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 9px', fontSize: 12, fontWeight: 500,
                background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                boxShadow: 'var(--shadow-xs)',
              }}
            >
              <Icon name="download" size={12} />PNG
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
